import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';
import {
  UserListItem,
  UsersListResponse,
  UpdateUserRequest,
  ResetPasswordRequest,
  UserRole,
} from '@/types/user';

// Query Keys
export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (filters: string) => [...userKeys.lists(), { filters }] as const,
  detail: (id: string) => [...userKeys.all, 'detail', id] as const,
};

// Fetch all users with pagination and filters
export function useUsers(params: {
  page: number;
  limit: number;
  search?: string;
  role?: UserRole;
  isActive?: boolean;
}) {
  return useQuery({
    queryKey: userKeys.list(JSON.stringify(params)),
    queryFn: async () => {
      const query = new URLSearchParams();
      query.set('page', String(params.page));
      query.set('limit', String(params.limit));
      if (params.search) query.set('search', params.search);
      if (params.role) query.set('role', params.role);
      if (params.isActive !== undefined) query.set('isActive', String(params.isActive));
      const response = await apiClient.get<{ success: boolean; data: UsersListResponse }>(`/users?${query.toString()}`);
      return response.data;
    },
  });
}

// Create user (reuse existing /auth/register endpoint)
export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      email: string;
      username: string;
      password: string;
      firstName?: string;
      lastName?: string;
      role: UserRole;
    }) => {
      const response = await apiClient.post<{ success: boolean; data: UserListItem }>('/auth/register', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      toast.success('User created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to create user');
    },
  });
}

// Update user
export function useUpdateUser(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateUserRequest) => {
      const response = await apiClient.put<{ success: boolean; data: UserListItem }>(`/users/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
      toast.success('User updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to update user');
    },
  });
}

// Reset user password (admin action)
export function useResetUserPassword(id: string) {
  return useMutation({
    mutationFn: async (data: ResetPasswordRequest) => {
      await apiClient.put(`/users/${id}/reset-password`, data);
    },
    onSuccess: () => {
      toast.success('Password reset successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to reset password');
    },
  });
}

// Deactivate user
export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/users/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      toast.success('User deactivated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to deactivate user');
    },
  });
}
